/**
 * ImageCardContent - Renders the visual content of an image card.
 */

import { GripVertical } from 'lucide-react';
import { Badge } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import { useAssetPackshotScore } from '@/components/v2/channel-readiness/PackshotScoresContext';
import type { ProductAsset } from '@/types/products';
import { AltTextGenButton } from './AltTextGenButton';

export interface ImageCardContentProps {
  asset: ProductAsset;
  index: number;
  onClick?: () => void;
  isOverlay?: boolean;
  dragHandleProps?: Record<string, unknown>;
  productId?: string;
  isGlobal?: boolean;
  /** Called with the generated alt-text so the parent can persist it via the existing slot-save path. */
  onAltTextGenerated?: (assetId: string, text: string) => void;
}

export function ImageCardContent({
  asset,
  index,
  onClick,
  isOverlay = false,
  dragHandleProps,
  productId,
  onAltTextGenerated,
}: ImageCardContentProps) {
  const imageUrl = asset.asset_id
    ? `/api/v1/asset/${asset.asset_id}/image`
    : '/images/placeholder-image.png';

  return (
    <StandardImageCard
      asset={asset}
      index={index}
      imageUrl={imageUrl}
      onClick={onClick}
      isOverlay={isOverlay}
      dragHandleProps={dragHandleProps}
      productId={productId}
      onAltTextGenerated={onAltTextGenerated}
    />
  );
}

/** Drag handle overlay shared between inherited and standard cards. */
function DragHandle({ index, dragHandleProps }: {
  index: number;
  dragHandleProps?: Record<string, unknown>;
}) {
  return (
    <div
      className={cn(
        'absolute top-2 left-2 w-8 h-8 rounded',
        'bg-black/70 text-white flex items-center justify-center',
        'cursor-grab z-10 hover:bg-black/90 transition-colors'
      )}
      {...dragHandleProps}
      aria-label={`Bild ${index + 1} verschieben`}
      role="button"
      tabIndex={0}
    >
      <GripVertical className="h-4 w-4" />
    </div>
  );
}

function StandardImageCard({ asset, index, imageUrl, onClick, isOverlay, dragHandleProps, productId, onAltTextGenerated }: {
  asset: ProductAsset;
  index: number;
  imageUrl: string;
  onClick?: () => void;
  isOverlay: boolean;
  dragHandleProps?: Record<string, unknown>;
  productId?: string;
  onAltTextGenerated?: (assetId: string, text: string) => void;
}) {
  return (
    <div
      className={cn(
        'relative group rounded-lg border-2 border-neutral-200 dark:border-neutral-700',
        'bg-white dark:bg-neutral-900 overflow-hidden',
        'hover:border-primary-500 dark:hover:border-primary-400 transition-colors',
        isOverlay && 'shadow-2xl rotate-2 scale-105'
      )}
      style={isOverlay ? { cursor: 'grabbing' } : undefined}
    >
      <DragHandle index={index} dragHandleProps={dragHandleProps} />
      <ImageContainer
        asset={asset}
        index={index}
        imageUrl={imageUrl}
        onClick={onClick}
      />
      {productId && onAltTextGenerated && (
        <AltTextGenButton
          productId={productId}
          assetId={asset.asset_id}
          onGenerated={(text) => onAltTextGenerated(asset.asset_id, text)}
        />
      )}
      {asset.alt_text && (
        <div
          className="px-2 py-1.5 text-xs text-neutral-600 dark:text-neutral-400 truncate border-t border-neutral-100 dark:border-neutral-800"
          title={asset.alt_text}
        >
          {asset.alt_text}
        </div>
      )}
    </div>
  );
}

function ImageContainer({ asset, index, imageUrl, onClick }: {
  asset: ProductAsset;
  index: number;
  imageUrl: string;
  onClick?: () => void;
}) {
  const packshotScore = useAssetPackshotScore(asset.asset_id);

  return (
    <div
      className="aspect-square cursor-pointer bg-neutral-100 dark:bg-neutral-800"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => onClick && e.key === 'Enter' && onClick()}
    >
      <img
        src={imageUrl}
        alt={asset.alt_text || `Bild Slot ${asset.slot}`}
        className="w-full h-full object-cover"
        loading="lazy"
        draggable={false}
      />
      <Badge variant="primary" className="absolute top-2 right-2">
        #{index + 1}
      </Badge>
      {asset.upload_source && (
        <Badge
          variant={asset.upload_source === 'user' ? 'success' : 'info'}
          className="absolute top-12 right-2 text-xs"
        >
          {asset.upload_source === 'user' ? 'Upload' : 'JTL'}
        </Badge>
      )}
      {packshotScore && <PackshotScoreOverlay score={packshotScore.score} category={packshotScore.category} />}
      {asset.width && asset.height && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity">
          {asset.width} x {asset.height}
        </div>
      )}
    </div>
  );
}

function PackshotScoreOverlay({ score, category }: { score: number; category: string }) {
  const variant = score >= 70 ? 'success' : score >= 40 ? 'warning' : 'error';
  const bg = {
    success: 'bg-emerald-500/90 text-white',
    warning: 'bg-amber-500/90 text-white',
    error: 'bg-red-500/90 text-white',
  }[variant];

  return (
    <div className={cn('absolute bottom-2 left-2 flex items-center gap-1 rounded px-1.5 py-0.5', bg)}>
      <span className="text-xs font-bold tabular-nums">{score}</span>
      <span className="text-2xs uppercase tracking-wider opacity-80">{category}</span>
    </div>
  );
}
